import React, {useEffect, useState, useRef, forwardRef, useImperativeHandle} from 'react';
import {
    Dialog,
    Flex,
    Text,
    Icon,
    PriorityIcon,
    Input,
    Button,
    Separator,
    Title
} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import {FiltersList} from './FiltersList';
import texts from '../texts';
import styles from '../styles/SuggestedSearchesDialog.module.scss';
import {convertFilterValsToText} from '../utils/personalization';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {Search} from '../../../_legacy/Searches/stores/Search';
import {
    User,
    IndexedSearch,
    SearchFilter,
    configuration
} from '../../../services/indexDB/indexeddbServiceSearches';
import {Form} from '../../../_legacy/Form/stores/Form';
import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {showConfirmMessage} from '../../../_shared/Messages/actions/MessageActions';
import messageTexts from '../../../_shared/Messages/texts';
import {keyCodes} from '../../../constants';

interface Props {
    db;
    search: Search;
    form: Form;
    popularSearch: IndexedSearch;
    user: User;
    handleSnooze: () => Promise<void>;
    handleReject: () => Promise<void>;
    handleSaveSearch: (name: string, isDefault: boolean) => Promise<void>;
    monitorNameChange: () => void;
}

const getFiltersTextValues = (search: IndexedSearch, form: Form) => {
    return search.filters.map((filter: SearchFilter) => {
        return convertFilterValsToText(form, filter);
    });
};

export const PersonalizationSearchesDialog = observer(
    forwardRef((children, ref) => {
        const [isShow, setIsShow] = useState<boolean>(false);
        const [terminate, setTerminate] = useState<boolean>(false);
        const [props, setProps] = useState<Props | null>(null);
        const [formName, setFormName] = useState<string>('');
        const [defaultTitle, setDefaultTitle] = useState<string>('');
        const [displayfilters, setDisplay] = useState<any[]>([]);
        const name = useRef('');
        const inputTag = 'save-search-input';

        useImperativeHandle(ref, () => {
            return {
                handleClose,
                showDialog
            };
        });

        const showDialog = (props: Props) => {
            if (props) {
                const {searchesStore} = formsStore;
                const emptyPlace = searchesStore && searchesStore.emptyPlace.toString();

                setProps(props);
                setFormName(props.form.name);
                emptyPlace && setDefaultTitle(texts.searchNamePlaceholder.replace('X', emptyPlace));
                setDisplay(getFiltersTextValues(props.popularSearch, props.form));
                setIsShow(true);
                if (!props.db && terminate) {
                    setTerminate(false);
                }
            }
        };

        useEffect(() => {
            isShow && window.addEventListener('keydown', handleKeyDown, true);
            return () => window.removeEventListener('keydown', handleKeyDown, true);
        }, [isShow]);

        const handleClose = () => {
            if (isShow) {
                setTerminate(true);
                setIsShow(false);
                window.removeEventListener('keydown', handleKeyDown, true);
                returnFocus();
            }
        };

        const handleKeyDown = event => {
            if (event.target.name === inputTag && event.keyCode === keyCodes.ENTER) {
                event.stopImmediatePropagation();
                event.preventDefault();
                onSaveSearch();
            }
        };

        useEffect(() => {
            if (props && formName) {
                const isActive = formName === formsStore?.currentForm?.name;
                setIsShow(isActive);
            }
        }, [formsStore?.currentForm]);

        const handleChange = e => {
            name.current = e.target.value;
        };

        const addNewSearch = async (name: string, isDefault: boolean, isNameUpdated: boolean) => {
            isNameUpdated && props.monitorNameChange();
            await props.handleSaveSearch(name, isDefault);
        };

        const onSaveSearch = () => {
            const searchTitle = name.current || defaultTitle;
            const isNameUpdated = name.current !== '';
            const {searchesStore} = formsStore;
            const {defaultIdx} = searchesStore;
            const formHasDefaultSearch = defaultIdx > 0;
            if (!formHasDefaultSearch) {
                showConfirmMessage({
                    title: messageTexts.setAsDefualt.replace('XXX', searchTitle),
                    confirmButtonText: messageTexts.yes,
                    cancleButtonText: messageTexts.no,
                    onConfirm: () => addNewSearch(searchTitle, true, isNameUpdated),
                    onCancel: () => addNewSearch(searchTitle, false, isNameUpdated),
                    defaultButton: 0
                });
            } else {
                addNewSearch(searchTitle, false, isNameUpdated);
            }
        };

        const onSnooze = async () => {
            await props.handleSnooze();
        };

        const onReject = async () => {
            await props.handleReject();
        };

        const DialogHeader = (
            <div className="dialog-drag-handle">
                <Flex componentClasses={styles.upperTitle} justifyContent="flex-start">
                    <Icon
                        componentClasses={styles.marginHorizontal}
                        icon={PriorityIcon.bulb}
                        iconColor="primary-700"
                    />
                    <Title skin="h5" textColor="primary-700" text={texts.upperTitle} />
                </Flex>
            </div>
        );

        const PreliminaryText = (
            <Text componentClasses={styles.suggestion} skin="paragraph-1" textColor="primary-700">
                {texts.suggestionPreliminaryText}
            </Text>
        );

        const searchNameEdit = (
            <Flex justifyContent="space-between" componentClasses={styles.nameEdit}>
                <Text
                    componentClasses={styles.searchNameLabel}
                    skin="paragraph-2"
                    textColor="primary-700"
                >
                    {texts.searchNameLabel}
                </Text>
                <Input
                    name={inputTag}
                    size="medium"
                    skin="box"
                    componentClasses={styles.placeholder}
                    placeholder={defaultTitle}
                    maxLength={configuration.maxSearchNameLength}
                    onChange={e => handleChange(e)}
                    autoFocus
                />
            </Flex>
        );

        const footer = (
            <>
                <Separator componentClasses={styles.marginVertical} align="vertical" height={1} />
                <Flex justifyContent="space-between">
                    <Flex justifyContent="flex-start">
                        <Button onClick={onSnooze} skin="text">
                            {texts.subForms_snooze}
                        </Button>
                        <Separator
                            componentClasses={styles.separator}
                            align="horizontal"
                            height={35}
                        />
                        <Button onClick={onReject} skin="text">
                            {texts.dontShowAgain}
                        </Button>
                    </Flex>
                    <Button size="small" onClick={onSaveSearch}>
                        {texts.saveSearch}
                    </Button>
                </Flex>
            </>
        );

        const Content = (
            <div>
                {PreliminaryText}
                {searchNameEdit}
                <FiltersList filters={displayfilters} />
                {footer}
            </div>
        );

        if (isShow && !terminate) {
            return (
                <Dialog
                    isOpen
                    content={Content}
                    title={DialogHeader}
                    componentClasses={styles.dialog}
                    width={640}
                    onClose={handleClose}
                    mask={false}
                    draggable
                    closable={false}
                />
            );
        } else {
            return null;
        }
    })
);
